import { apiClient, apiClientPublic } from "services/api-client"
import { getTenant } from "utils/tenant_utils"

export const getCompanies = async () => {
    return await apiClient.get(`/company`)
}

export const getCompaniesPaged = async (page, perPage) => {
    return await apiClient.get(`/company/paged?page=${page}&perPage=${perPage}`)
}

export const getCompanyById = async (id) => {
    return await apiClient.get(`/company/${id}`)
}

export const getCompanyBySlug = async (slug) => {
    return await apiClient.get(`/company/by-slug/${slug}`)
}

export const getCompanyShortBySlug = async (slug) => {
    return await apiClientPublic.get(`/company/short-data-by-slug/${slug}`)
}

export const getMapCompanyBySlug = async (slug) => {
    return await apiClient.get(`/company/map-by-slug/${slug}`)
}

export const getCompanyRolesBySlug = async (slug) => {
    return await apiClient.get(`/company/roles-by-slug/${slug}`)
}

export const addCompany = async (data) => {
    return await apiClient.post('/company', data)
}

export const editCompany = async (id, data) => {
    return await apiClient.put(`/company/${id}`, data)
}

/**
 * LAYER 1 OF PROTECTION - Frontend Whitelist
 *
 * Edit company profile with limited fields
 * Only allows: name, corporateName, description, size, income, industry, domains
 * Protected fields like CNPJ, signedPlan, roles, etc. are NOT sent to the backend
 *
 * @param {string} id - Company ID
 * @param {object} data - Company profile data (will be filtered by caller)
 * @returns {Promise} - API response
 */
export const editCompanyProfile = async (id, data) => {
    return await apiClient.put(`/company/${id}/profile`, data)
}

export const deleteCompany = async (id) => {
    return await apiClient.delete(`/company/${id}`)
}

export const getCompaniesByPartner = async (tenantId) => {
    return await apiClient.get(`/company/by-partner/${tenantId}`)
}

export const getCompaniesByPartnerPaged = async (tenantId, page, perPage) => {
    return await apiClient.get(`/company/by-partner/${tenantId}?page=${page}&per-page=${perPage}`)
}

export const createCompany = async (data, idOnboard) => {
    return await apiClientPublic.post(`/company/onboarding/${idOnboard}`, data)
}

export const editCompanyPlan = async (slug, data) => {
    return await apiClientPublic.put(`/company/onboarding/${slug}`, data)
}

export const getPresetPlan = async (slug) => {
    return await apiClientPublic.get(`/company/site/preset-plan/${slug}`,)
}

export const getCountryConfigs = async () => {
    return await apiClientPublic.get('/company/country-config')
}

export const getOpsDashboard = async (page = 1, perPage = 20) => {
    return await apiClient.get(`/company/ops-dashboard?page=${page}&perPage=${perPage}`)
}

export const getPartnerDashboard = async (page = 1, perPage = 20, companyId = null) => {
    const params = new URLSearchParams({ page, perPage });
    if (companyId) params.append('companyId', companyId);
    return await apiClient.get(`/company/ops-dashboard/partner?${params}`)
}

export const partnerOnboarding = async (data) => {
    return await apiClient.post('/company/partner-onboarding', data)
}

export const getBranding = async () => {
    return await apiClient.get('/company/branding')
}

/**
 * Query function para o React Query — recebe o QueryFunctionContext com a query key.
 * Key esperada: ['company-branding', slug]
 *
 * - Se o slug for do próprio tenant (ou ausente): usa o endpoint autenticado.
 * - Se for outro tenant: usa o endpoint público e normaliza a resposta para o mesmo shape.
 */
export const getBrandingForDisplay = async ({ queryKey }) => {
    const slug = queryKey?.[1];
    const ownSlug = getTenant();

    // On localhost getTenant() returns 'www' — always use authenticated endpoint
    // (passing ?slug= for non-own tenants so the backend returns the right company).
    // On staging/prod, own tenant also uses authenticated endpoint.
    if (!slug || slug === ownSlug || ownSlug === 'www') {
        const params = slug && ownSlug === 'www' ? `?slug=${slug}` : '';
        return apiClient.get(`/company/branding${params}`);
    }

    // Staging/prod, different tenant → public endpoint
    const res = await apiClientPublic.get(`/company/branding/public/${slug}`);
    if (res.data?.default) {
        return { data: { branding: null } };
    }
    return {
        data: {
            branding: {
                primaryColor: res.data.primaryColor ?? null,
                secondaryColor: res.data.secondaryColor ?? null,
                logoUrl: res.data.logoUrl ?? null,
                logoMiniUrl: res.data.logoMiniUrl ?? null,
                logoDarkUrl: res.data.logoDarkUrl ?? null,
                logoMiniDarkUrl: res.data.logoMiniDarkUrl ?? null,
                faviconUrl: res.data.faviconUrl ?? null,
            },
        },
    };
}

export const updateBranding = async (data, slug = null) => {
    const params = slug ? `?slug=${slug}` : '';
    return await apiClient.put(`/company/branding${params}`, data)
}

export const resetBranding = async (slug = null) => {
    const params = slug ? `?slug=${slug}` : '';
    return await apiClient.delete(`/company/branding${params}`)
}

export const uploadBrandingLogo = async (file, slug = null) => {
    const formData = new FormData()
    formData.append('logo', file)
    const params = slug ? `?slug=${slug}` : '';
    return await apiClient.post(`/company/branding/logo${params}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    })
}

export const uploadBrandingLogoMini = async (file, slug = null) => {
    const formData = new FormData()
    formData.append('logo-mini', file)
    const params = slug ? `?slug=${slug}` : '';
    return await apiClient.post(`/company/branding/logo-mini${params}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    })
}

export const uploadBrandingLogoDark = async (file, slug = null) => {
    const formData = new FormData()
    formData.append('logo-dark', file)
    const params = slug ? `?slug=${slug}` : '';
    return await apiClient.post(`/company/branding/logo-dark${params}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    })
}

export const uploadBrandingLogoMiniDark = async (file, slug = null) => {
    const formData = new FormData()
    formData.append('logo-mini-dark', file)
    const params = slug ? `?slug=${slug}` : '';
    return await apiClient.post(`/company/branding/logo-mini-dark${params}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    })
}

export const uploadBrandingFavicon = async (file, slug = null) => {
    const formData = new FormData()
    formData.append('favicon', file)
    const params = slug ? `?slug=${slug}` : '';
    return await apiClient.post(`/company/branding/favicon${params}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    })
}

export const getPublicBranding = async (companyKey) => {
    return await apiClientPublic.get(`/company/branding/public/${companyKey}`)
}